/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module SharedModule
 */

import {
    Directive,
    Inject,
    OnDestroy,
    Optional,
} from '@angular/core';

import { NgControl } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';
import { BREADCRUMB_DESCRIPTION$ } from 'ng/shared/components/full-modal/full-modal.tokens';

/**
 * @description Directive used to tie an ngModel to a Full Modal breadcrumb description.
 * @example
 *     <input
 *         name="name"
 *         [(ngModel)]="editable.config.name"
 *         clrInput
 *         placeholder="{{ globalL10nKeys.namePlaceholder | vtranslate }}"
 *         setBreadcrumbDescription
 *         required
 *     />
 * @author alextsg
 */
@Directive({
    selector: '[setBreadcrumbDescription]',
})
export class SetBreadcrumbDescriptionDirective implements OnDestroy {
    private ngDestroyed$ = new Subject();

    constructor(
        control: NgControl,
        @Inject(BREADCRUMB_DESCRIPTION$)
        @Optional()
        breadcrumbDescription$: Subject<string>,
    ) {
        if (breadcrumbDescription$ === null) {
            throw new Error('setBreadcrumbDescription used without breadcrumbDescription$');
        }

        control.valueChanges
            .pipe(takeUntil(this.ngDestroyed$))
            .subscribe(value => breadcrumbDescription$.next(value));
    }

    /** @override */
    public ngOnDestroy(): void {
        this.ngDestroyed$.next(true);
        this.ngDestroyed$.complete();
    }
}
